/**
 * Lowercase all dictionary keys so lookups are case-insensitive regardless of
 * how the admin entered them (e.g. "3XM" still matches a query of "3xm").
 * Values for keys that collide after lowercasing are merged.
 */
export function normalizeSynonyms(synonyms) {
    const normalized = {};
    for (const [key, values] of Object.entries(synonyms)) {
        const lowerKey = key.toLowerCase();
        normalized[lowerKey] = normalized[lowerKey]
            ? Array.from(new Set([...normalized[lowerKey], ...values]))
            : values;
    }
    return normalized;
}
/**
 * Build a reverse lookup: lowercased synonym value → lowercased canonical key.
 */
function buildReverseMap(synonyms) {
    const reverseMap = new Map();
    for (const [key, values] of Object.entries(synonyms)) {
        for (const value of values) {
            reverseMap.set(value.toLowerCase(), key.toLowerCase());
        }
    }
    return reverseMap;
}
/**
 * Resolve the synonym alternatives for a single (already-lowercased) query word:
 * the group's values when the word is a canonical key, or the canonical key and
 * its sibling values when the word is itself a synonym value. Returned as whole,
 * lowercased phrases (not split into individual words).
 */
function lookupSynonymPhrases(word, synonyms, reverseMap) {
    const phrases = [];
    if (synonyms[word]) {
        synonyms[word].forEach(s => phrases.push(s.toLowerCase()));
    }
    // Skip the reverse-lookup expansion when `word` itself begins with the canonical
    // key (e.g. "2534" is a value of "25", and "2534".startsWith("25")). In that case
    // `word` is a more specific completion of the abbreviation, so its sibling values
    // (e.g. "2524") and the abbreviation itself (e.g. "25") are DIFFERENT things, not
    // synonyms of `word` — including them would make searching "2534" also match
    // products for "2524" or anything literally containing "25" (e.g. "D-Sub 25").
    const canonicalKey = reverseMap.get(word);
    if (canonicalKey && !word.startsWith(canonicalKey)) {
        phrases.push(canonicalKey);
        synonyms[canonicalKey]?.forEach(s => phrases.push(s.toLowerCase()));
    }
    return phrases;
}
/**
 * Expand a search query with synonyms (bidirectional).
 * Whether the query word is a key or a value in the synonyms map,
 * the entire synonym group is added to the expanded query.
 */
export function expandQuery(query, synonyms) {
    if (!query.trim() || Object.keys(synonyms).length === 0) {
        return query;
    }
    const reverseMap = buildReverseMap(synonyms);
    const words = query.toLowerCase().split(/\s+/);
    const expandedTerms = new Set(words);
    words.forEach(word => {
        lookupSynonymPhrases(word, synonyms, reverseMap).forEach(phrase => expandedTerms.add(phrase));
    });
    return Array.from(expandedTerms).join(' ');
}
/**
 * Collect the literal phrase alternatives (kept whole, not split into individual
 * words) that the synonym dictionary considers equivalent to any word in the query.
 *
 * This is used to verify that a synonym-based match actually contains a *complete*
 * equivalent phrase rather than just one word of it — e.g. searching "xlm" (mapped
 * to "XLR male") must not loosely match a product titled "XLR female" merely
 * because both phrases share the word "XLR".
 */
/**
 * Return the synonym values registered under a purely-numeric dictionary key
 * matching the (trimmed, lowercased) query — e.g. typing "2" surfaces
 * ["2524","2534",...] for similarly-numbered Mogami cable models. Limited to
 * numeric keys so the rest of the synonym dictionary (abbreviations, connector
 * names, etc.) isn't exposed as search suggestions.
 */
export function getNumericSynonymSuggestions(query, synonyms) {
    const key = query.trim().toLowerCase();
    if (!/^\d+$/.test(key))
        return [];
    return synonyms[key] || [];
}
export function getSynonymPhrases(query, synonyms) {
    if (!query.trim() || Object.keys(synonyms).length === 0) {
        return [];
    }
    const reverseMap = buildReverseMap(synonyms);
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const phrases = new Set();
    words.forEach(word => {
        lookupSynonymPhrases(word, synonyms, reverseMap).forEach(phrase => phrases.add(phrase));
    });
    return Array.from(phrases);
}
